<template>
  <div class="animated fadeIn">
    <b-card>
      <div slot="header">
        <i class="fa fa-bar-chart"></i> Clinical activity report
        <b-badge variant="success" class="ml-2">NEW</b-badge>
      </div>

      <b-row class="form-group">
        <b-col sm="12" md="3">
          <label for="fromDate">From</label>
          <input id="fromDate" type="date" class="form-control" v-model="fromDate" :max="toDate">
        </b-col>
        <b-col sm="12" md="3">
          <label for="toDate">To</label>
          <input id="toDate" type="date" class="form-control" v-model="toDate" :min="fromDate">
        </b-col>
        <b-col sm="12" md="6" class="d-flex align-items-end">
          <b-button variant="primary" class="mr-2" :disabled="loading" @click="getReport()">
            <i class="fa fa-refresh"></i> Apply
          </b-button>
          <b-button variant="secondary" class="mr-2" :disabled="loading" @click="applyPreset(1)">
            Today
          </b-button>
          <b-button variant="secondary" class="mr-2" :disabled="loading" @click="applyPreset(30)">
            Last 30 days
          </b-button>
          <b-button variant="secondary" class="mr-2" :disabled="loading" @click="applyPreset(90)">
            Last 90 days
          </b-button>
          <b-button variant="outline-primary" :disabled="loading || !report" @click="exportCsv()">
            <i class="fa fa-download"></i> Export CSV
          </b-button>
        </b-col>
      </b-row>

      <b-alert show variant="light" class="clinical-activity-note mb-3" v-if="report">
        <strong>How consultations are counted</strong>
        <p class="mb-2">
          The <strong>reported</strong> consultation total excludes bookings where the patient's
          <strong>last name is Test</strong> (developer test appointments). Other metrics are unchanged.
        </p>
        <div class="clinical-activity-breakdown" v-if="consultationBreakdown">
          <div class="clinical-activity-breakdown-item">
            <span class="label">Reported consultations</span>
            <strong>{{ formatNumber(consultationBreakdown.reported) }}</strong>
            <small>{{ formatNumber(consultationBreakdown.reportedVideo) }} video · {{ formatNumber(consultationBreakdown.reportedChat) }} chat</small>
          </div>
          <div class="clinical-activity-breakdown-item muted">
            <span class="label">Excluded (last name Test)</span>
            <strong>{{ formatNumber(consultationBreakdown.excluded) }}</strong>
            <small>{{ formatNumber(consultationBreakdown.excludedVideo) }} video · {{ formatNumber(consultationBreakdown.excludedChat) }} chat</small>
          </div>
          <div class="clinical-activity-breakdown-item">
            <span class="label">Raw total in system</span>
            <strong>{{ formatNumber(consultationBreakdown.raw) }}</strong>
            <small>reported + excluded</small>
          </div>
        </div>
      </b-alert>

      <b-row v-if="report">
        <b-col sm="6" lg="3" v-for="row in summaryRows" :key="row.label">
          <b-card no-body class="text-center">
            <b-card-body>
              <h2>{{ formatNumber(row.total) }}</h2>
              <p class="mb-0 text-muted">{{ row.label }}</p>
              <small class="text-muted">
                {{ formatNumber(row.video) }} video &middot; {{ formatNumber(row.chat) }} chat
              </small>
              <small class="text-muted d-block mt-1" v-if="row.footnote">{{ row.footnote }}</small>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-table :items="tableRows" :fields="columnNames" hover outlined show-empty responsive
               :busy="loading" empty-text="No activity for the selected period" class="mt-3">
        <template v-slot:cell(video)="data">{{ formatNumber(data.item.video) }}</template>
        <template v-slot:cell(chat)="data">{{ formatNumber(data.item.chat) }}</template>
        <template v-slot:cell(total)="data">
          <strong :class="{ 'text-muted': data.item.muted }">{{ formatNumber(data.item.total) }}</strong>
        </template>
        <template v-slot:cell(label)="data">
          <span :class="{ 'text-muted': data.item.muted }">{{ data.item.label }}</span>
        </template>
      </b-table>

      <p class="text-muted mb-0">
        <small>
          Video consultations are counted on the date the appointment took place; every other
          figure is counted on the date the document was issued. Prescriptions exclude drafts and
          those marked medically unsafe. Fit notes and referral notes are not counted if the
          clinician later deleted them. Blood tests are not included because the system does not
          record blood tests being ordered or performed.
        </small>
      </p>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  data: function () {
    return {
      loading: false,
      report: null,
      fromDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
      toDate: moment().format('YYYY-MM-DD'),
      columnNames: [
        { key: 'label', label: 'Metric' },
        { key: 'video', label: 'Video consultations' },
        { key: 'chat', label: 'Chat assessments' },
        { key: 'total', label: 'Total' }
      ]
    }
  },
  computed: {
    consultationBreakdown () {
      if (!this.report) {
        return null
      }
      const reported = this.counts(this.report.consultations)
      const excluded = this.counts(this.report.consultationsExcludedTestPatients)
      return {
        reported: reported.total,
        reportedVideo: reported.video,
        reportedChat: reported.chat,
        excluded: excluded.total,
        excludedVideo: excluded.video,
        excludedChat: excluded.chat,
        raw: reported.total + excluded.total
      }
    },
    summaryRows () {
      if (!this.report) {
        return []
      }
      const excludedTotal = this.consultationBreakdown ? this.consultationBreakdown.excluded : 0
      return [
        {
          label: 'Booked',
          ...this.counts(this.report.booked),
          footnote: 'Booked, not yet completed or cancelled'
        },
        {
          label: 'Completed',
          ...this.counts(this.report.consultations),
          footnote: excludedTotal > 0
            ? `Excludes ${this.formatNumber(excludedTotal)} with last name Test`
            : 'Excludes last name Test'
        },
        { label: 'Prescriptions', ...this.counts(this.report.prescriptions) },
        { label: 'Fit notes', ...this.counts(this.report.fitNotes) },
        { label: 'Referral notes', ...this.counts(this.report.referralNotes) }
      ]
    },
    tableRows () {
      if (!this.report) {
        return []
      }
      const rows = [
        { label: 'Booked', ...this.counts(this.report.booked) },
        { label: 'Completed consultations (reported)', ...this.counts(this.report.consultations) },
        { label: 'Prescriptions', ...this.counts(this.report.prescriptions) },
        { label: 'Fit notes', ...this.counts(this.report.fitNotes) },
        { label: 'Referral notes', ...this.counts(this.report.referralNotes) }
      ]
      const excluded = this.counts(this.report.consultationsExcludedTestPatients)
      if (excluded.total > 0) {
        rows.splice(2, 0, {
          label: 'Consultations excluded (last name Test)',
          ...excluded,
          muted: true
        })
        rows.splice(3, 0, {
          label: 'Consultations raw total',
          video: this.counts(this.report.consultations).video + excluded.video,
          chat: this.counts(this.report.consultations).chat + excluded.chat,
          total: this.counts(this.report.consultations).total + excluded.total,
          muted: true
        })
      }
      return rows
    }
  },
  created () {
    this.getReport()
  },
  methods: {
    counts (metric) {
      return {
        video: (metric && metric.video) || 0,
        chat: (metric && metric.chat) || 0,
        total: (metric && metric.total) || 0
      }
    },
    formatNumber (value) {
      return Number(value || 0).toLocaleString()
    },
    applyPreset (days) {
      this.toDate = moment().format('YYYY-MM-DD')
      this.fromDate = moment().subtract(days - 1, 'days').format('YYYY-MM-DD')
      this.getReport()
    },
    getReport () {
      this.loading = true
      this.$clinicalReportService.getActivityReport(this.fromDate, this.toDate).then(report => {
        this.report = report
      }, error => {
        this.$awn.alert('Could not load the clinical activity report', 'An error occurred')
        console.log(error)
      }).then(() => {
        this.loading = false
      })
    },
    exportCsv () {
      var lines = [
        ['Clinical activity report'],
        ['From', this.report.fromDate],
        ['To', this.report.toDate],
        [],
        ['Metric', 'Video consultations', 'Chat assessments', 'Total']
      ]
      this.tableRows.forEach(row => {
        lines.push([row.label, row.video, row.chat, row.total])
      })
      if (this.consultationBreakdown) {
        lines.push([])
        lines.push(['Consultation counting note', 'Reported totals exclude patient last name Test'])
        lines.push([
          'Excluded test consultations',
          this.consultationBreakdown.excludedVideo,
          this.consultationBreakdown.excludedChat,
          this.consultationBreakdown.excluded
        ])
      }

      var csv = lines.map(line => line.join(',')).join('\n')
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      var url = window.URL.createObjectURL(blob)
      var link = document.createElement('a')
      link.href = url
      link.download = 'clinical-activity-' + this.report.fromDate + '-to-' + this.report.toDate + '.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.clinical-activity-note {
  border: 1px solid #d5e0da;
  background: #f7faf8;
}

.clinical-activity-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
}

.clinical-activity-breakdown-item {
  background: #fff;
  border: 1px solid #e3ebe6;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}

.clinical-activity-breakdown-item .label {
  display: block;
  font-size: 0.78rem;
  color: #64748b;
  margin-bottom: 0.15rem;
}

.clinical-activity-breakdown-item strong {
  display: block;
  font-size: 1.35rem;
  color: #17333a;
}

.clinical-activity-breakdown-item small {
  display: block;
  color: #64748b;
  margin-top: 0.15rem;
}

.clinical-activity-breakdown-item.muted strong {
  color: #64748b;
}
</style>
